import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useMatchesHandleData } from '~/modules/root/hook';
import { DetailLogoBadge } from '~/ui-rocco/pages/detail-shared';
import { CONTENT_CAP } from '~/ui-rocco/pages/detail-shared/large-screen';
import { useProfileRailDocked } from '../hooks/use-profile-rail-dock';

interface ProfessionalDockingBadgeProps {
    src: string;
    alt: string;
    background?: string | null;
    link: string;
    ariaLabel: string;
}

/**
 * The detail pages' badge on the banner's edge, which on the profile's tabs
 * follows the reader into the tab band.
 *
 * It starts on the same pixel as {@link DetailBadgeRow}'s badge. The box is
 * sticky at the offset that puts its centre on the band's 46px midline, so it
 * locks just before the band does and the two land together; once the band
 * is docked the badge shrinks about its own centre to fit inside it, beside
 * the CTA pair, and rides there for the rest of the page.
 *
 * The box keeps its full 164px through all of it — the negative margins give
 * that height back to the flow, as the badge row's do — so shrinking never
 * pulls the page up under the reader. It scales rather than resizes so the
 * badge's ring and inner gap shrink with it.
 */
export const ProfessionalDockingBadge = ({
    src,
    alt,
    background,
    link,
    ariaLabel,
}: ProfessionalDockingBadgeProps) => {
    const docked = useProfileRailDocked();
    // Public pages re-enable the badge under the row's pointer-events-none;
    // the Business Manager preview leaves it off so its lock holds.
    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    return (
        <div
            className={cn(
                CONTENT_CAP,
                // Above the band (115) and the CTA pair (120) it docks beside.
                'pointer-events-none sticky z-[121] flex justify-end px-[3rem] max-md:hidden',
                // Half its 164px onto the banner, the other half given back.
                'md:-mt-[5.125rem] md:mb-[-5.125rem]',
                // Centre on the band's midline: 46px − 82px.
                'md:top-[-2.25rem]',
            )}
        >
            <RoccoLink
                to={link}
                aria-label={ariaLabel}
                className={cn(
                    'block origin-center transition-transform duration-300 ease-out md:mr-[3.3125rem]',
                    !isBMPreview && 'pointer-events-auto',
                    // 72px docked: the 92px band with 10px clear either side.
                    docked ? 'scale-[0.439]' : 'scale-100',
                )}
            >
                <DetailLogoBadge src={src} alt={alt} background={background} />
            </RoccoLink>
        </div>
    );
};
